import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';

import { RevealDirective } from '@ledgerly/common/browser';
import { JsonLdService } from '@ledgerly/shared/layout';

export interface Faq {
  question: string;
  answer: string;
}

/**
 * The FAQ.
 *
 * Five of the old entries were wrong about the product and two broke the
 * privacy rule. Recorded here because they will otherwise creep back:
 *
 * - "track transactions from multiple accounts, credit cards, and other
 *   financial accounts in one place" implied a bank connection. There is none.
 * - "Ledgerly automatically categorizes your expenses". It does not.
 * - "provides insights or advice on saving money" overstated one pace insight
 *   and the period review into financial advice.
 * - "Security is our top priority... never shared with third parties" was an
 *   unqualified absolute with no backup carve-out.
 * - "Absolutely! Ledgerly automatically..." The register is wrong on top of
 *   the facts. The app does not use exclamation marks.
 *
 * Every answer below is checkable against the app. If one stops being true,
 * the app is right and this file is stale.
 */
@Component({
  selector: 'app-faqs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RevealDirective],
  templateUrl: './faqs.component.html',
})
export class FaqsComponent implements OnDestroy {
  private readonly jsonLd = inject(JsonLdService);

  protected readonly faqs: Faq[] = [
    {
      question: $localize`:@@faqBankQ:Does Ledgerly connect to my bank?`,
      answer: $localize`:@@faqBankA:No, and that is on purpose. You add each transaction, or you write a rule that adds it for you. It means the app is only as up to date as you keep it. It also means there is no bank login to hand over, and nobody else holding a copy of your statements.`,
    },
    {
      question: $localize`:@@faqCategoriseQ:Does it sort my spending into categories for me?`,
      answer: $localize`:@@faqCategoriseA:No. You pick the category as you add the transaction. Guessing gets it wrong often enough that fixing it takes longer than choosing, and only you know whether a supermarket trip was the weekly shop or a present.`,
    },
    {
      question: $localize`:@@faqAdviceQ:Will it tell me how to save money?`,
      answer: $localize`:@@faqAdviceA:No. Ledgerly shows you where you are. What you can spend today, how each category is doing, and how the last period went. It does not give advice, score your habits or set goals for you.`,
    },
    {
      question: $localize`:@@faqDataQ:Where is my data kept?`,
      answer: $localize`:@@faqDataA:In a file on your phone. There is no account and no server holding it. The only thing that copies it off the phone is backup, to Google Drive, iCloud or a file, and that stays off until you switch it on. The app also sends crash reports and a few usage events to Sentry, and handles purchases through RevenueCat. Neither of those sees your figures.`,
    },
    {
      question: $localize`:@@faqNewPhoneQ:Can I move to a new phone?`,
      answer: $localize`:@@faqNewPhoneA:Yes, if you have a backup. Restore it on the new phone and everything comes back. Your purchase moves separately, because it belongs to your App Store or Google Play account. Tap Restore on any device you sign in to.`,
    },
    {
      question: $localize`:@@faqTrialQ:What happens if I do not pay?`,
      answer: $localize`:@@faqTrialA:You can still read, search, export and back up. Paying is what lets you add new transactions. Nothing you have already written down becomes unreadable, and the CSV export keeps working whether you buy or not.`,
    },
    {
      question: $localize`:@@faqFreeQ:Is there a free version?`,
      answer: $localize`:@@faqFreeA:No. It is free to start, with everything unlocked, and then it is a paid app. Monthly, yearly, or one payment for good. There is no second tier, so nothing is being saved up to sell you later.`,
    },
    {
      question: $localize`:@@faqCurrencyQ:Which currencies can I use?`,
      answer: $localize`:@@faqCurrencyA:165. You pick one and every figure in the app is shown in it. Ledgerly does not convert between currencies, because a budget only makes sense in the one you actually think in.`,
    },
    {
      question: $localize`:@@faqLanguageQ:What languages does the app speak?`,
      answer: $localize`:@@faqLanguageA:English, French, German, Italian and Spanish. The app and this website speak the same five, so whatever brought you here is what you will see once you open it.`,
    },
    {
      question: $localize`:@@faqExportQ:Can I get my data out?`,
      answer: $localize`:@@faqExportA:Yes. Tools, then Export data. Pick a budget and the dates, and those transactions come out as a CSV that opens in any spreadsheet. It keeps working after a subscription stops.`,
    },
  ];

  /**
   * The same questions as structured data.
   *
   * Built from `faqs`, so it is in the reader's language and can only say
   * what the page says. Google shows FAQ results for few sites now, but the
   * markup is still how a crawler or an assistant knows which sentence
   * answers which question. These ten are the plainest statement of what
   * Ledgerly will not do, which is exactly what an assistant gets wrong.
   */
  private readonly ld = this.jsonLd.set('faq', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: this.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  });

  ngOnDestroy(): void {
    this.jsonLd.remove('faq');
  }
}
